import { Component } from '@theme/component';
import { onDocumentLoaded, changeMetaThemeColor, setHeaderMenuStyle } from '@theme/utilities';

/**
 * @typedef {Object} HeaderComponentRefs
 * @property {HTMLDivElement} headerDrawerContainer - The header drawer container element
 * @property {HTMLElement} headerMenu - The header menu element
 * @property {HTMLElement} headerRowTop - The header top row element
 */

/**
 * @typedef {CustomEvent<{ minimumReached: boolean }>} OverflowMinimumEvent
 */

/**
 * Return a reliable ResizeObserver block size across browsers.
 *
 * @param {ResizeObserverEntry} entry
 * @returns {number}
 */
function getObservedBlockSize(entry) {
  const borderBoxSize = entry.borderBoxSize;

  if (Array.isArray(borderBoxSize) && borderBoxSize[0]) {
    return borderBoxSize[0].blockSize;
  }

  if (
    borderBoxSize &&
    !Array.isArray(borderBoxSize) &&
    'blockSize' in borderBoxSize
  ) {
    return borderBoxSize.blockSize;
  }

  return (
    entry.contentRect?.height ??
    entry.target.getBoundingClientRect().height ??
    0
  );
}

/**
 * A custom element that manages the site header.
 *
 * This version is resilient to Shopify/GemPages detaching and reinserting
 * the header during editor refreshes and client-side page transitions.
 *
 * @extends {Component<HeaderComponentRefs>}
 */
class HeaderComponent extends Component {
  requiredRefs = ['headerDrawerContainer', 'headerMenu', 'headerRowTop'];

  /** @type {number | null} */
  #menuDrawerHiddenWidth = null;

  /** @type {IntersectionObserver | null} */
  #intersectionObserver = null;

  /** @type {boolean} */
  #offscreen = false;

  /** @type {number} */
  #lastScrollTop = 0;

  /** @type {number | null} */
  #timeout = null;

  /** @type {number | null} */
  #scrollRafId = null;

  /** @type {boolean} */
  #connectedReady = false;

  /**
   * Keeps the global --header-height property current.
   */
  #resizeObserver = new ResizeObserver(([entry]) => {
    if (!entry || !this.isConnected) return;

    const roundedHeaderHeight = Math.round(getObservedBlockSize(entry));

    document.body.style.setProperty(
      '--header-height',
      `${Math.max(0, roundedHeaderHeight)}px`
    );

    if (
      this.#menuDrawerHiddenWidth &&
      window.innerWidth > this.#menuDrawerHiddenWidth
    ) {
      this.#updateMenuVisibility(false);
    }
  });

  /**
   * Observe when the header reaches or leaves its natural position.
   *
   * @param {boolean} alwaysSticky
   */
  #observeStickyPosition = (alwaysSticky = true) => {
    this.#intersectionObserver?.disconnect();
    this.#intersectionObserver = null;

    this.#intersectionObserver = new IntersectionObserver(
      ([entry]) => {
        if (!entry || !this.isConnected) return;

        const scrollTop =
          document.scrollingElement?.scrollTop ?? window.scrollY ?? 0;

        if (alwaysSticky) {
          this.dataset.stickyState = entry.isIntersecting
            ? 'inactive'
            : 'active';

          if (entry.isIntersecting) {
            this.dataset.scrollDirection = 'none';
          }

          if (this.dataset.themeColor) {
            changeMetaThemeColor(this.dataset.themeColor);
          }

          return;
        }

        this.#offscreen = !entry.isIntersecting;

        if (entry.isIntersecting || scrollTop <= 0) {
          this.#offscreen = false;
          this.dataset.stickyState = 'inactive';
          this.dataset.scrollDirection = 'none';
        }
      },
      {
        threshold: alwaysSticky ? 1 : 0,
      }
    );

    this.#intersectionObserver.observe(this);
  };

  /**
   * @param {OverflowMinimumEvent} event
   */
  #handleOverflowMinimum = (event) => {
    this.#updateMenuVisibility(event.detail.minimumReached);
  };

  /**
   * @param {boolean} hideMenu
   */
  #updateMenuVisibility(hideMenu) {
    this.#menuDrawerHiddenWidth = hideMenu ? window.innerWidth : null;
    setHeaderMenuStyle();
  }

  #handleWindowScroll = () => {
    if (this.#scrollRafId !== null) return;

    this.#scrollRafId = requestAnimationFrame(() => {
      this.#scrollRafId = null;

      if (!this.isConnected) return;

      this.#updateScrollState();
    });
  };

  #updateScrollState = () => {
    const stickyMode = this.getAttribute('sticky');

    if (!stickyMode) return;

    const scrollTop =
      document.scrollingElement?.scrollTop ?? window.scrollY ?? 0;

    const previousScrollTop = this.#lastScrollTop;
    const isScrollingUp = scrollTop < previousScrollTop;
    const isScrollingDown = scrollTop > previousScrollTop;
    const headerTop = this.getBoundingClientRect().top;
    const isAtTop = scrollTop <= 0 || headerTop >= 0;

    this.#lastScrollTop = scrollTop;

    if (this.#timeout) {
      clearTimeout(this.#timeout);
      this.#timeout = null;
    }

    if (stickyMode === 'always') {
      if (isAtTop) {
        this.dataset.stickyState = 'inactive';
        this.dataset.scrollDirection = 'none';
      } else {
        this.dataset.stickyState = 'active';

        if (isScrollingUp) {
          this.dataset.scrollDirection = 'up';
        } else if (isScrollingDown) {
          this.dataset.scrollDirection = 'down';
        } else {
          this.dataset.scrollDirection = 'none';
        }
      }

      return;
    }

    if (stickyMode !== 'scroll-up') return;

    if (isAtTop) {
      this.#offscreen = false;
      this.dataset.stickyState = 'inactive';
      this.dataset.scrollDirection = 'none';
      return;
    }

    if (!this.#offscreen) {
      this.dataset.stickyState = 'inactive';
      this.dataset.scrollDirection = 'none';
      return;
    }

    if (isScrollingUp) {
      this.dataset.stickyState = 'active';
      this.dataset.scrollDirection = 'up';
    } else if (isScrollingDown) {
      this.dataset.stickyState = 'idle';
      this.dataset.scrollDirection = 'down';
    }
  };

  connectedCallback() {
    super.connectedCallback();

    if (this.#connectedReady) return;

    this.#connectedReady = true;
    this.#offscreen = false;

    this.#lastScrollTop =
      document.scrollingElement?.scrollTop ?? window.scrollY ?? 0;

    this.dataset.scrollDirection = 'none';

    this.#resizeObserver.observe(this);

    this.addEventListener(
      'overflowMinimum',
      this.#handleOverflowMinimum
    );

    const stickyMode = this.getAttribute('sticky');

    if (stickyMode) {
      this.dataset.stickyState = 'inactive';

      this.#observeStickyPosition(stickyMode === 'always');

      if (stickyMode === 'scroll-up' || stickyMode === 'always') {
        document.addEventListener(
          'scroll',
          this.#handleWindowScroll,
          { passive: true }
        );
      }
    } else {
      delete this.dataset.stickyState;
      delete this.dataset.scrollDirection;
    }

    requestAnimationFrame(() => {
      if (!this.isConnected) return;

      setHeaderMenuStyle();
      this.#updateScrollState();

      requestAnimationFrame(() => {
        if (!this.isConnected) return;

        setHeaderMenuStyle();
        this.#updateScrollState();
      });
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#connectedReady = false;

    this.#resizeObserver.disconnect();

    this.#intersectionObserver?.disconnect();
    this.#intersectionObserver = null;

    this.removeEventListener(
      'overflowMinimum',
      this.#handleOverflowMinimum
    );

    document.removeEventListener(
      'scroll',
      this.#handleWindowScroll
    );

    if (this.#scrollRafId !== null) {
      cancelAnimationFrame(this.#scrollRafId);
      this.#scrollRafId = null;
    }

    if (this.#timeout) {
      clearTimeout(this.#timeout);
      this.#timeout = null;
    }

    this.#offscreen = false;
    this.#menuDrawerHiddenWidth = null;

    requestAnimationFrame(() => {
      const liveHeader = document.querySelector('header-component');

      if (
        !(liveHeader instanceof HTMLElement) ||
        !liveHeader.isConnected
      ) {
        document.body.style.setProperty('--header-height', '0px');
      }
    });
  }
}

if (!customElements.get('header-component')) {
  customElements.define('header-component', HeaderComponent);
}

/**
 * Header-group observer manager.
 *
 * Reconnects observers when Shopify or GemPages replaces the header group.
 */
let observedHeaderGroup = null;
let headerGroupResizeObserver = null;
let headerGroupMutationObserver = null;
let documentHeaderMutationObserver = null;
let headerGroupSetupFrame = null;

/**
 * Calculate the complete current header-group height from the live DOM.
 *
 * @param {HTMLElement} headerGroup
 */
function updateHeaderGroupHeight(headerGroup) {
  if (!headerGroup.isConnected) return;

  const header = headerGroup.querySelector('header-component');

  let totalHeight = 0;

  for (const element of headerGroup.children) {
    if (!(element instanceof HTMLElement)) continue;

    const shouldInclude =
      element !== header ||
      (
        header instanceof HTMLElement &&
        header.hasAttribute('transparent') &&
        Boolean(header.parentElement?.nextElementSibling)
      );

    if (shouldInclude) {
      totalHeight += element.getBoundingClientRect().height;
    }
  }

  document.body.style.setProperty(
    '--header-group-height',
    `${Math.max(0, Math.round(totalHeight))}px`
  );
}

/**
 * Observe all current header-group children.
 *
 * @param {HTMLElement} headerGroup
 */
function observeHeaderGroupChildren(headerGroup) {
  if (!headerGroupResizeObserver) return;

  for (const element of headerGroup.children) {
    if (element instanceof HTMLElement) {
      headerGroupResizeObserver.observe(element);
    }
  }

  updateHeaderGroupHeight(headerGroup);
}

function setupHeaderGroupObservers() {
  const nextHeaderGroup = document.querySelector('#header-group');

  if (!(nextHeaderGroup instanceof HTMLElement)) {
    observedHeaderGroup = null;

    headerGroupResizeObserver?.disconnect();
    headerGroupMutationObserver?.disconnect();

    document.body.style.setProperty(
      '--header-group-height',
      '0px'
    );

    return;
  }

  if (
    observedHeaderGroup === nextHeaderGroup &&
    nextHeaderGroup.isConnected
  ) {
    observeHeaderGroupChildren(nextHeaderGroup);
    return;
  }

  headerGroupResizeObserver?.disconnect();
  headerGroupMutationObserver?.disconnect();

  observedHeaderGroup = nextHeaderGroup;

  headerGroupResizeObserver = new ResizeObserver(() => {
    if (observedHeaderGroup?.isConnected) {
      updateHeaderGroupHeight(observedHeaderGroup);
    }
  });

  headerGroupMutationObserver = new MutationObserver(() => {
    if (!observedHeaderGroup?.isConnected) {
      scheduleHeaderGroupSetup();
      return;
    }

    observeHeaderGroupChildren(observedHeaderGroup);
    setHeaderMenuStyle();
  });

  headerGroupMutationObserver.observe(nextHeaderGroup, {
    childList: true,
    subtree: false,
  });

  observeHeaderGroupChildren(nextHeaderGroup);
}

/**
 * Coalesce replacement and layout mutations.
 */
function scheduleHeaderGroupSetup() {
  if (headerGroupSetupFrame !== null) return;

  headerGroupSetupFrame = requestAnimationFrame(() => {
    headerGroupSetupFrame = null;
    setupHeaderGroupObservers();
  });
}

onDocumentLoaded(() => {
  setupHeaderGroupObservers();

  documentHeaderMutationObserver?.disconnect();

  documentHeaderMutationObserver = new MutationObserver(() => {
    const currentHeaderGroup =
      document.querySelector('#header-group');

    if (
      currentHeaderGroup !== observedHeaderGroup ||
      !observedHeaderGroup?.isConnected
    ) {
      scheduleHeaderGroupSetup();
    }
  });

  documentHeaderMutationObserver.observe(document.body, {
    childList: true,
    subtree: true,
  });

  window.addEventListener(
    'pageshow',
    () => {
      scheduleHeaderGroupSetup();

      requestAnimationFrame(() => {
        setHeaderMenuStyle();
      });
    },
    { passive: true }
  );
});